import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Product } from '../types/Product';
import { normalizeText } from '../utils/text';

interface ProductContextType {
  products: Product[];
  filteredProducts: Product[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  selectedCategory: string;
  priceRange: [number, number];
  setSearchTerm: (term: string) => void;
  setSelectedCategory: (category: string) => void;
  setPriceRange: (range: [number, number]) => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/mercadona_analysis.json');
        const data = await response.json();
        
        if (!data.products || !Array.isArray(data.products)) {
          throw new Error('Invalid product data');
        }

        const uniqueProducts = Array.from(
          new Map(data.products.map((p: Product) => [p.id, p])).values()
        );
        setProducts(uniqueProducts);
      } catch (error) {
        console.error('Error loading products:', error);
        setError('Error cargando productos');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search filter
      if (searchTerm.length >= 3) {
        const normalizedSearch = normalizeText(searchTerm);
        const normalizedName = normalizeText(product.display_name);
        const normalizedBrand = normalizeText(product.brand);
        const normalizedCategory = normalizeText(product.category_name);

        const matchesSearch = 
          normalizedName.includes(normalizedSearch) ||
          normalizedBrand.includes(normalizedSearch) ||
          normalizedCategory.includes(normalizedSearch);

        if (!matchesSearch) return false;
      }

      // Category filter
      if (selectedCategory && !normalizeText(product.category_name).includes(normalizeText(selectedCategory))) {
        return false;
      }

      // Price filter
      return product.current_price >= priceRange[0] && 
             product.current_price <= priceRange[1];
    });
  }, [products, searchTerm, selectedCategory, priceRange]);

  return (
    <ProductContext.Provider
      value={{
        products,
        filteredProducts,
        loading,
        error,
        searchTerm,
        selectedCategory,
        priceRange,
        setSearchTerm,
        setSelectedCategory,
        setPriceRange,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};